/**
 * Encryption + signing primitives for the integrations module (WebCrypto only, so the same
 * code runs in Workers, Node 20+ and browsers).
 *
 *  - OAuth tokens and webhook signing secrets are encrypted with AES-256-GCM using a
 *    per-workspace key derived (HKDF-SHA256) from the master `TOKEN_ENCRYPTION_KEY`.
 *    A leaked ciphertext from one workspace can never be decrypted with another workspace's key.
 *  - Outgoing webhooks are signed with HMAC-SHA256 (`BuildBee-Signature: t=..,v1=..`).
 *  - OAuth `state` values are HMAC-signed, expiring JSON envelopes.
 */

const enc = new TextEncoder();
const dec = new TextDecoder();

// ---------------------------------------------------------------------------
// Base64 helpers
// ---------------------------------------------------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

export function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function utf8ToBase64Url(value: string): string {
  return bytesToBase64Url(enc.encode(value));
}

export function base64UrlToUtf8(value: string): string {
  return dec.decode(base64ToBytes(value));
}

function toHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Constant-time string comparison. */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

// ---------------------------------------------------------------------------
// Master key + per-workspace keys
// ---------------------------------------------------------------------------

export class EncryptionKeyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EncryptionKeyError';
  }
}

/**
 * Resolve the 32-byte master key. `TOKEN_ENCRYPTION_KEY` must be base64 of 32 random bytes
 * (`openssl rand -base64 32`). Outside production a missing key falls back to a key derived from
 * `fallbackSecret` (e.g. FILE_SIGNING_SECRET) so local dev and tests work without setup.
 */
export async function resolveMasterKey(opts: {
  tokenEncryptionKey?: string | null;
  fallbackSecret?: string | null;
  production: boolean;
}): Promise<Uint8Array<ArrayBuffer>> {
  if (opts.tokenEncryptionKey) {
    let bytes: Uint8Array<ArrayBuffer>;
    try {
      bytes = base64ToBytes(opts.tokenEncryptionKey.trim());
    } catch {
      throw new EncryptionKeyError('TOKEN_ENCRYPTION_KEY must be base64');
    }
    if (bytes.length !== 32) throw new EncryptionKeyError('TOKEN_ENCRYPTION_KEY must decode to exactly 32 bytes');
    return bytes;
  }
  if (opts.production) throw new EncryptionKeyError('TOKEN_ENCRYPTION_KEY is required in production');
  if (!opts.fallbackSecret) throw new EncryptionKeyError('TOKEN_ENCRYPTION_KEY is not configured');
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(`buildbee-dev-token-key:${opts.fallbackSecret}`));
  return new Uint8Array(digest);
}

const keyCache = new Map<string, Promise<CryptoKey>>();

/** AES-GCM key for one workspace: HKDF-SHA256(master, salt = workspaceId, info = purpose). */
export function workspaceKey(master: Uint8Array<ArrayBuffer>, workspaceId: string, purpose = 'buildbee:integrations:v1'): Promise<CryptoKey> {
  const cacheKey = `${bytesToBase64(master)}|${workspaceId}|${purpose}`;
  let key = keyCache.get(cacheKey);
  if (!key) {
    key = (async () => {
      const base = await crypto.subtle.importKey('raw', master, 'HKDF', false, ['deriveKey']);
      return crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt: enc.encode(workspaceId), info: enc.encode(purpose) },
        base,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
      );
    })();
    if (keyCache.size > 500) keyCache.clear();
    keyCache.set(cacheKey, key);
  }
  return key;
}

const CIPHER_VERSION = 'v1';

/** Encrypt a UTF-8 string: `v1:<iv b64url>:<ciphertext b64url>`. The workspace id is bound as AAD. */
export async function encryptString(key: CryptoKey, plaintext: string, aad: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: enc.encode(aad) }, key, enc.encode(plaintext));
  return `${CIPHER_VERSION}:${bytesToBase64Url(iv)}:${bytesToBase64Url(new Uint8Array(ct))}`;
}

export async function decryptString(key: CryptoKey, payload: string, aad: string): Promise<string> {
  const [version, ivPart, ctPart] = payload.split(':');
  if (version !== CIPHER_VERSION || !ivPart || !ctPart) throw new EncryptionKeyError('Unsupported ciphertext format');
  try {
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(ivPart), additionalData: enc.encode(aad) },
      key,
      base64ToBytes(ctPart),
    );
    return dec.decode(pt);
  } catch {
    throw new EncryptionKeyError('Could not decrypt credentials (wrong key or tampered data)');
  }
}

/** Encrypts/decrypts JSON values for one workspace. */
export type WorkspaceVault = {
  encrypt: (value: unknown) => Promise<string>;
  decrypt: <T = unknown>(payload: string) => Promise<T>;
};

export async function workspaceVault(master: Uint8Array<ArrayBuffer>, workspaceId: string): Promise<WorkspaceVault> {
  const key = await workspaceKey(master, workspaceId);
  return {
    encrypt: (value) => encryptString(key, JSON.stringify(value), workspaceId),
    decrypt: async <T>(payload: string) => JSON.parse(await decryptString(key, payload, workspaceId)) as T,
  };
}

// ---------------------------------------------------------------------------
// HMAC
// ---------------------------------------------------------------------------

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
}

export async function hmacSha256Hex(secret: string, message: string): Promise<string> {
  return toHex(await crypto.subtle.sign('HMAC', await hmacKey(secret), enc.encode(message)));
}

export async function hmacSha256Base64Url(secret: string, message: string): Promise<string> {
  return bytesToBase64Url(new Uint8Array(await crypto.subtle.sign('HMAC', await hmacKey(secret), enc.encode(message))));
}

/**
 * Shared secret for inbound push endpoints (Gmail Pub/Sub token, Graph `clientState`), derived
 * from the server signing secret so API and workers agree without another env var.
 */
export function pushSecret(serverSecret: string, purpose: string): Promise<string> {
  return hmacSha256Base64Url(serverSecret, `buildbee-push:${purpose}`);
}

// ---------------------------------------------------------------------------
// Webhook signatures
// ---------------------------------------------------------------------------

export const WEBHOOK_SIGNATURE_HEADER = 'BuildBee-Signature';
export const WEBHOOK_SECRET_PREFIX = 'whsec_';

/** Random endpoint signing secret, shown to the user once. */
export function generateWebhookSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return `${WEBHOOK_SECRET_PREFIX}${bytesToBase64Url(bytes)}`;
}

/** `t=<unix seconds>,v1=<hex HMAC-SHA256(secret, "<t>.<body>")>` (Stripe-style, replay-resistant). */
export async function signWebhookPayload(secret: string, body: string, timestampSeconds: number): Promise<string> {
  const sig = await hmacSha256Hex(secret, `${timestampSeconds}.${body}`);
  return `t=${timestampSeconds},v1=${sig}`;
}

/** Verify a signature header (what receivers do; also used in tests and docs). */
export async function verifyWebhookSignature(
  secret: string,
  body: string,
  header: string | null | undefined,
  options: { nowSeconds?: number; toleranceSeconds?: number } = {},
): Promise<boolean> {
  if (!header) return false;
  const parts = Object.fromEntries(
    header.split(',').map((p) => {
      const i = p.indexOf('=');
      return [p.slice(0, i).trim(), p.slice(i + 1).trim()];
    }),
  ) as Record<string, string | undefined>;
  const t = Number(parts.t);
  if (!Number.isFinite(t) || !parts.v1) return false;
  const now = options.nowSeconds ?? Math.floor(Date.now() / 1000);
  if (Math.abs(now - t) > (options.toleranceSeconds ?? 300)) return false;
  const expected = await hmacSha256Hex(secret, `${t}.${body}`);
  return timingSafeEqual(expected, parts.v1);
}

// ---------------------------------------------------------------------------
// Signed, expiring envelopes (OAuth state)
// ---------------------------------------------------------------------------

export async function signEnvelope(secret: string, payload: Record<string, unknown>, ttlSeconds: number, nowMs = Date.now()): Promise<string> {
  const body = utf8ToBase64Url(JSON.stringify({ ...payload, exp: Math.floor(nowMs / 1000) + ttlSeconds }));
  return `${body}.${await hmacSha256Base64Url(secret, body)}`;
}

export async function openEnvelope<T extends Record<string, unknown>>(secret: string, value: string, nowMs = Date.now()): Promise<T | null> {
  const [body, sig] = value.split('.');
  if (!body || !sig) return null;
  const expected = await hmacSha256Base64Url(secret, body);
  if (!timingSafeEqual(expected, sig)) return null;
  try {
    const parsed = JSON.parse(base64UrlToUtf8(body)) as T & { exp?: number };
    if (typeof parsed.exp !== 'number' || parsed.exp * 1000 < nowMs) return null;
    return parsed;
  } catch {
    return null;
  }
}
